import { jwtDecode } from 'jwt-decode';
import { getApiBaseUrl as getApiBaseUrlFromEnv, resolveEnvironment } from '@seede/api-client';
import { Amplitude } from '@seede/amplitude';
import {
  getToken as getTokenFromStorage,
  setToken as setTokenToStorage,
  removeToken as removeTokenFromStorage,
} from '../lib/token';
import { brevoIdentify, brevoTrack, brevoDateNow, requestBrevo } from '../lib/brevo';
import {
  getMetaPixelUserData,
  hasTrackedMetaPixelEvent,
  markMetaPixelEventTracked,
  trackCompleteRegistration,
} from '../lib/meta-pixel';

const getApiBaseUrl = (): string => getApiBaseUrlFromEnv(resolveEnvironment());

export interface User {
  id: string;
  email: string;
  name?: string;
  username?: string;
  avatar?: string;
  email_verified?: boolean;
  verified?: boolean;
}

export interface Usage {
  amount: number;
  balance: number;
  paid: number;
  free: number;
  total: number;
  used: number;
  remaining: number;
  subscription_plan?: string | null;
  subscription_status?: string | null;
  subscription_until?: string | null;
  request_id?: string;
}

export interface Checkout {
  id: string;
  url?: string;
  status?: string;
  amount?: number;
  currency?: string;
  metadata?: Record<string, any>;
}

interface JwtPayload {
  id: string;
  email: string;
  name?: string;
  scope?: string;
  project_id?: string;
  exp?: number;
  iat?: number;
}

export interface SignInParams {
  email: string;
  password: string;
}

export interface SignUpParams {
  email: string;
  password: string;
  emailCode: string;
  inviteCode?: string;
  language?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

export interface SendVerificationCodeParams {
  email: string;
  language?: string;
}

export interface ForgotPasswordParams {
  email: string;
}

export interface ResetPasswordParams {
  email: string;
  code: string;
  newPassword: string;
}

export const useAuth = () => {
  const syncAmplitudeIdentity = () => {
    if (!import.meta.client || !user.value?.id) return;
    Amplitude.identify(user.value.id, {
      user_id: user.value.id,
      email: user.value.email,
      name: user.value.name,
      email_verified: user.value.email_verified,
      verified: user.value.verified,
    });
    // `$setOnce`: stamp the first time we ever see this user, never overwrite.
    Amplitude.setUserPropertiesOnce({
      first_seen_at: new Date().toISOString(),
      signup_source:
        ((user.value as unknown as { signup_source?: string }).signup_source as string) || 'web',
    });
    // `$add`: track total login sessions across devices atomically (don't
    // compute client-side, that loses concurrent updates).
    Amplitude.addUserProperties({ total_logins: 1 });
  };

  const syncAmplitudeUsageProperties = () => {
    if (!import.meta.client || !user.value?.id) return;

    // Normalize the raw server status into the canonical 4-state taxonomy
    // documented in docs/amplitude-events.md (active / trialing / canceled /
    // expired). Everything else collapses to `inactive` so dashboards have a
    // closed set of values.
    const rawStatus = (usage.value.subscription_status || '').toLowerCase();
    const rawUntil = usage.value.subscription_until || null;
    let normalizedStatus: 'active' | 'trialing' | 'canceled' | 'expired' | 'inactive' = 'inactive';
    if (rawStatus === 'active') normalizedStatus = 'active';
    else if (rawStatus === 'trialing' || rawStatus === 'trial') normalizedStatus = 'trialing';
    else if (
      rawStatus === 'canceled' ||
      rawStatus === 'cancelled' ||
      rawStatus === 'cancel_at_period_end'
    ) {
      // Stripe-style "canceled but still in current period" — treat as
      // canceled if `until` is in the past, otherwise active-cancelled.
      const untilMs = rawUntil ? new Date(rawUntil).getTime() : 0;
      normalizedStatus = untilMs && untilMs < Date.now() ? 'expired' : 'canceled';
    } else if (rawStatus === 'expired' || rawStatus === 'past_due' || rawStatus === 'unpaid') {
      normalizedStatus = 'expired';
    } else if (rawStatus === 'incomplete' || rawStatus === 'incomplete_expired') {
      normalizedStatus = 'inactive';
    }

    const plan = (usage.value.subscription_plan || 'free').toLowerCase();
    const isPaid = normalizedStatus === 'active' || normalizedStatus === 'trialing';

    Amplitude.setUserProperties({
      current_subscription_plan: plan,
      current_subscription_status: normalizedStatus,
      subscription_until: rawUntil ?? undefined,
      // Convenience boolean — `is_paid_now` is monotonic per session, used to
      // segment "currently active paid users" without parsing status strings
      // on the dashboard.
      is_paid_now: isPaid,
      credits_total: usage.value.total,
      credits_used: usage.value.used,
      credits_remaining: usage.value.remaining,

      // Legacy names — kept for one release so existing dashboards don't
      // break. Remove after migration.
      subscription_plan: plan,
      subscription_status: normalizedStatus,
    });

    // `ever_paid` is a true-once flag: once a user has ever had a paid
    // (non-free) plan, we set it forever via $setOnce so cohort analysis
    // can distinguish "tried paid then churned" vs "never paid".
    if (isPaid && plan !== 'free') {
      Amplitude.setUserPropertiesOnce({
        ever_paid: true,
        first_ever_paid_at: new Date().toISOString(),
        first_ever_paid_plan: plan,
      });
    }
  };

  // Use useState to ensure shared state across all components
  const user = useState<User | null>('auth.user', () => null);
  const usage = useState<Usage>('auth.usage', () => ({
    amount: 0,
    balance: 0,
    paid: 0,
    free: 0,
    total: 0,
    used: 0,
    remaining: 0,
    subscription_plan: null,
    subscription_status: null,
    subscription_until: null,
  }));
  const isLoading = useState<boolean>('auth.isLoading', () => false);
  const isInitialized = useState<boolean>('auth.isInitialized', () => false);

  // 使用独立的 token 模块，确保一致性
  const getToken = (): string | null => {
    return getTokenFromStorage();
  };

  const setToken = (token: string) => {
    setTokenToStorage(token);
  };

  const removeToken = () => {
    removeTokenFromStorage();
    if (import.meta.client) {
      Amplitude.reset();
    }
    user.value = null;
    usage.value = {
      amount: 0,
      balance: 0,
      paid: 0,
      free: 0,
      total: 0,
      used: 0,
      remaining: 0,
      subscription_plan: null,
      subscription_status: null,
      subscription_until: null,
    };
  };

  const getAuthHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  /**
   * 同步解析 JWT token 获取基础用户信息
   * @returns 解析后的用户基础信息，如果 token 无效或过期返回 null
   */
  const parseToken = (token: string): User | null => {
    try {
      const decoded = jwtDecode<JwtPayload>(token);

      // 检查 token 是否过期
      if (decoded.exp && decoded.exp * 1000 < Date.now()) {
        console.warn('Token expired');
        return null;
      }

      // 返回从 JWT 解析的基础用户信息
      return {
        id: decoded.id,
        email: decoded.email,
        name: decoded.name,
      };
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  };

  /**
   * 异步获取完整用户信息
   * 如果是 401/403 错误，会取消授权（移除 token 和用户信息）
   * 其他错误（网络问题等）不会清除 token
   */
  const fetchUserInfo = async () => {
    const token = getToken();
    if (!token) {
      user.value = null;
      return;
    }

    isLoading.value = true;
    try {
      const api = getApiBaseUrl();
      const url: string = `${api}/user/info`;
      // 使用类型断言避免 Nuxt $fetch 的路由类型推断问题
      const data = (await ($fetch as any)(url, {
        headers: {
          ...getAuthHeaders(),
        },
      })) as User;
      // 合并完整用户信息（包括 Google 头像等）
      user.value = data;
      syncAmplitudeIdentity();
    } catch (error: any) {
      console.error('Failed to fetch user info:', error);
      // 只有在认证相关错误时才清除 token
      const status = error?.response?.status || error?.status || error?.statusCode;
      if (status === 401 || status === 403) {
        // Token 无效或过期，清除授权
        removeToken();
        user.value = null;
      }
      // 其他错误（网络问题等）保留现有用户信息，不清除 token
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * 异步获取用户用量信息
   */
  const fetchUsage = async () => {
    const token = getToken();
    if (!token) {
      usage.value = {
        amount: 0,
        balance: 0,
        paid: 0,
        free: 0,
        total: 0,
        used: 0,
        remaining: 0,
        subscription_plan: null,
        subscription_status: null,
        subscription_until: null,
      };
      return;
    }

    try {
      const api = getApiBaseUrl();
      const url: string = `${api}/billing/usage`;
      const data = (await ($fetch as any)(url, {
        headers: {
          ...getAuthHeaders(),
        },
      })) as Usage;

      const total = Number((data as any).total ?? data.amount) || 0;
      const remaining = Number((data as any).remaining ?? data.balance) || 0;
      const used = Number((data as any).used ?? Math.max(0, total - remaining)) || 0;

      usage.value = {
        amount: Number(data.amount) || 0,
        balance: Number(data.balance) || 0,
        paid: Number(data.paid) || 0,
        free: Number(data.free) || 0,
        total,
        used,
        remaining,
        subscription_plan: (data as any).subscription_plan ?? null,
        subscription_status: (data as any).subscription_status ?? null,
        subscription_until: (data as any).subscription_until ?? null,
        request_id: data.request_id,
      };
      syncAmplitudeUsageProperties();
    } catch (error) {
      console.error('Failed to fetch usage:', error);
      usage.value = {
        amount: 0,
        balance: 0,
        paid: 0,
        free: 0,
        total: 0,
        used: 0,
        remaining: 0,
        subscription_plan: null,
        subscription_status: null,
        subscription_until: null,
      };
    }
  };

  /**
   * 更新用户信息
   * @param patch 更新的字段
   */
  const updateUser = async (patch: Partial<User>) => {
    const api = getApiBaseUrl();
    const url: string = `${api}/user`;

    const updatedUser = (await ($fetch as any)(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: patch,
    })) as Partial<User>;

    if (updatedUser && typeof updatedUser === 'object' && user.value) {
      user.value = {
        ...user.value,
        ...updatedUser,
      };
      syncAmplitudeIdentity();
    }

    return updatedUser;
  };

  /**
   * 初始化认证状态
   * 1. 同步从 JWT 解析基础用户信息（立即可用）
   * 2. 异步获取完整用户信息，失败则取消授权
   */
  const initAuth = async () => {
    if (isInitialized.value) return;

    const token = getToken();
    if (!token) {
      user.value = null;
      return;
    }

    // 同步解析 token 获取基础信息
    const parsedUser = parseToken(token);
    if (!parsedUser) {
      // token 无效或过期，清除
      removeToken();
      return;
    }

    // 立即设置基础用户信息
    user.value = parsedUser;

    // 异步获取完整用户信息，失败会自动取消授权
    fetchUserInfo();
    await fetchUsage();
    isInitialized.value = true;

    if (import.meta.client && user.value?.email) {
      const plan = usage.value?.subscription_plan || 'free';
      const attrs: Record<string, unknown> = {
        FIRSTNAME: user.value.name || '',
        LAST_ACTIVE_DATE: brevoDateNow(),
        CURRENT_PLAN: plan,
      };
      if (plan !== 'free') {
        attrs.FIRST_PAID_DATE = brevoDateNow();
      }
      brevoIdentify(user.value.email, attrs);
      requestBrevo('/brevo/identify', {
        email: user.value.email || '',
        attributes: attrs,
      });
    }
  };

  /**
   * 用户登录
   * @param params 登录参数（email, password）
   * @returns 登录成功返回 true，失败抛出错误
   */
  const signIn = async (params: SignInParams): Promise<boolean> => {
    const api = getApiBaseUrl();
    const url: string = `${api}/sign-in`;
    // 使用类型断言避免 Nuxt $fetch 的路由类型推断问题
    const response = (await ($fetch as any)(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: {
        email: params.email.trim().toLowerCase(),
        password: params.password,
      },
    })) as { token?: string };

    if (response.token) {
      setToken(response.token);
      await fetchUserInfo();
      await fetchUsage();
      if (user.value?.email) {
        const plan = usage.value?.subscription_plan || 'free';
        const attrs: Record<string, unknown> = {
          FIRSTNAME: user.value.name || '',
          LAST_ACTIVE_DATE: brevoDateNow(),
          CURRENT_PLAN: plan,
        };
        if (plan !== 'free') {
          attrs.FIRST_PAID_DATE = brevoDateNow();
        }
        brevoIdentify(user.value.email, attrs);
        brevoTrack('login', { method: 'email' });
        requestBrevo('/brevo/identify-track-event', {
          email: user.value.email || '',
          attributes: attrs,
          event: 'api-login',
          properties: { method: 'email' },
        });
      }
      return true;
    }

    throw new Error('No token received');
  };

  /**
   * 用户注册
   * @param params 注册参数（email, password, emailCode, language）
   * @returns 注册成功返回 true，失败抛出错误
   */
  const signUp = async (params: SignUpParams): Promise<boolean> => {
    const api = getApiBaseUrl();
    const url: string = `${api}/sign-up`;
    // 使用类型断言避免 Nuxt $fetch 的路由类型推断问题
    const response = (await ($fetch as any)(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: {
        email: params.email.trim().toLowerCase(),
        password: params.password,
        emailCode: params.emailCode.trim().toUpperCase(),
        inviteCode: params.inviteCode?.trim() || '',
        language: params.language || 'en',
        utm_source: params.utm_source,
        utm_medium: params.utm_medium,
        utm_campaign: params.utm_campaign,
        utm_term: params.utm_term,
        utm_content: params.utm_content,
      },
    })) as { token?: string };

    if (response.token) {
      setToken(response.token);
      await fetchUserInfo();
      fetchUsage();
      Amplitude.event('signup_completed', {
        method: 'email',
        user_id: user.value?.id || '',
      });
      if (user.value?.email) {
        const d = brevoDateNow();
        const attrs: Record<string, unknown> = {
          FIRSTNAME: user.value.name || '',
          SIGNUP_DATE: d,
          LAST_ACTIVE_DATE: d,
          CURRENT_PLAN: 'free',
        };
        brevoIdentify(user.value.email, attrs);
        brevoTrack('signup', { method: 'email' });
        requestBrevo('/brevo/identify-track-event', {
          email: user.value.email || '',
          attributes: attrs,
          event: 'api-signup',
          properties: { method: 'email' },
        });
      }
      const completeRegistrationKey = user.value?.id || user.value?.email;
      if (
        completeRegistrationKey &&
        !hasTrackedMetaPixelEvent('complete-registration', completeRegistrationKey)
      ) {
        trackCompleteRegistration({
          custom_data: {
            method: 'email',
          },
          user_data: getMetaPixelUserData(user.value),
        })
          .then(() => {
            markMetaPixelEventTracked('complete-registration', completeRegistrationKey);
          })
          .catch((error) => {
            console.error('Failed to track Meta Pixel complete registration:', error);
          });
      }
      return true;
    }

    throw new Error('No token received');
  };

  /**
   * 发送邮箱验证码
   * @param params 参数（email, language）
   */
  const sendVerificationCode = async (params: SendVerificationCodeParams): Promise<void> => {
    const api = getApiBaseUrl();
    const url: string = `${api}/account/send-email-verification`;
    await ($fetch as any)(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: {
        email: params.email.trim().toLowerCase(),
        language: params.language || 'en',
      },
    });
  };

  /**
   * 发送重置密码验证码
   * @param params 参数（email）
   */
  const forgotPassword = async (params: ForgotPasswordParams): Promise<void> => {
    const api = getApiBaseUrl();
    const url: string = `${api}/api/user/forgot`;
    await ($fetch as any)(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: {
        email: params.email.trim().toLowerCase(),
      },
    });
  };

  /**
   * 重置密码
   * @param params 参数（email, code, newPassword）
   */
  const resetPassword = async (params: ResetPasswordParams): Promise<void> => {
    const api = getApiBaseUrl();
    const url: string = `${api}/api/user/reset-password`;
    await ($fetch as any)(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: {
        email: params.email.trim().toLowerCase(),
        code: params.code.trim().toUpperCase(),
        newPassword: params.newPassword,
      },
    });
  };

  /**
   * 用户登出
   */
  const signOut = () => {
    removeToken();
  };

  const isAuthenticated = computed(() => !!user.value && !!getToken());

  const subscription = computed(() => {
    const status = usage.value?.subscription_status ?? null;
    return {
      plan: usage.value?.subscription_plan ?? null,
      status,
      until: usage.value?.subscription_until ?? null,
      isPaid: !!status && ['active', 'trialing'].includes(status),
    };
  });

  /**
   * 确保用户已认证
   * 统一的鉴权方法，业务页面直接调用即可
   * @returns true 如果已认证，false 如果未认证（会自动重定向到登录页）
   */
  const ensureAuth = async (): Promise<boolean> => {
    // 1. 检查是否有 token
    const token = getToken();
    if (!token) {
      return false;
    }

    // 2. 如果已有用户信息，直接返回成功
    if (user.value) {
      // 异步刷新用户信息，不阻塞
      fetchUserInfo().catch(() => {});
      return true;
    }

    // 3. 没有用户信息，尝试从 token 解析
    const parsedUser = parseToken(token);
    if (!parsedUser) {
      // token 无效或过期
      removeToken();
      return false;
    }

    // 4. 设置基础用户信息
    user.value = parsedUser;

    // 5. 异步获取完整用户信息（不阻塞）
    fetchUserInfo().catch(() => {});
    fetchUsage().catch(() => {});

    return true;
  };

  const getCheckout = async (id: string): Promise<Checkout> => {
    const token = getToken();
    if (!token) {
      throw new Error('Unauthorized');
    }
    const api = getApiBaseUrl();
    const url: string = `${api}/checkout/${encodeURIComponent(id)}`;
    return (await ($fetch as any)(url, {
      headers: {
        ...getAuthHeaders(),
      },
    })) as Checkout;
  };

  // Initialize user on mount if token exists (only once)
  if (import.meta.client && !user.value) {
    initAuth();
  }

  return {
    user: readonly(user),
    usage: readonly(usage),
    isLoading: readonly(isLoading),
    isAuthenticated,
    subscription,
    getToken,
    setToken,
    removeToken,
    fetchUserInfo,
    fetchUsage,
    initAuth,
    ensureAuth,
    getCheckout,
    signIn,
    signUp,
    signOut,
    sendVerificationCode,
    forgotPassword,
    resetPassword,
    updateUser,
  };
};
